import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Download, Smartphone, Share, Plus, X } from "lucide-react";

const STORAGE_KEY = "ldrakek_pwa_prompt_dismissed_v1";
const IS_INSTALLED_KEY = "ldrakek_pwa_installed";

const detectPlatform = () => {
  if (typeof window === "undefined") return "other";
  const ua = window.navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/i.test(ua);
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  if (isStandalone) return "installed";
  if (isIOS) return "ios";
  if (isAndroid) return "android";
  return "desktop";
};

const InstallPWAButton = ({ asMenuItem = false }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [platform, setPlatform] = useState("other");
  const [shouldNudge, setShouldNudge] = useState(false);

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);

    // Capture native `beforeinstallprompt` event (Android Chrome / desktop)
    const onBeforePrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforePrompt);

    // Detect post-install
    const onInstalled = () => {
      try { localStorage.setItem(IS_INSTALLED_KEY, "1"); } catch { /* ignore */ }
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    // Decide whether to nudge the user once
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      const installed = localStorage.getItem(IS_INSTALLED_KEY);
      if (!dismissed && !installed && p !== "installed" && (p === "ios" || p === "android")) {
        setShouldNudge(true);
      }
    } catch { /* ignore */ }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforePrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismissNudge = () => {
    setShouldNudge(false);
    try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch { /* ignore */ }
  };

  const openInstallDialog = () => {
    setShowDialog(true);
    setShouldNudge(false);
  };

  const handleNativeInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch (err) { /* ignore */ }
    setDeferredPrompt(null);
    setShowDialog(false);
  };

  // Don't render anything when already installed
  if (platform === "installed") return null;

  const trigger = asMenuItem ? (
    <button
      type="button"
      onClick={openInstallDialog}
      className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-accent text-left"
      data-testid="install-pwa-menu-item"
    >
      <Download className="h-4 w-4" />
      Namesti aplikacijo
    </button>
  ) : (
    <Button
      variant="outline"
      size="sm"
      onClick={openInstallDialog}
      data-testid="install-pwa-button"
    >
      <Download className="h-4 w-4 mr-1.5" />
      Namesti aplikacijo
    </Button>
  );

  return (
    <>
      {trigger}

      {/* Subtle bottom-nudge banner (only first visit on mobile) */}
      {shouldNudge && !showDialog && (
        <div
          className="fixed bottom-3 left-3 right-3 z-50 sm:left-auto sm:right-4 sm:max-w-sm rounded-lg border border-primary/30 bg-white shadow-lg px-3 py-2.5 flex items-center gap-2"
          data-testid="install-pwa-nudge"
        >
          <Smartphone className="h-5 w-5 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-foreground leading-tight">
              Dodaj LD Rakek na domači zaslon
            </p>
            <p className="text-[10px] text-muted-foreground leading-tight">
              Hitri dostop kot do aplikacije
            </p>
          </div>
          <Button size="sm" variant="default" className="h-7 text-xs" onClick={openInstallDialog} data-testid="install-pwa-nudge-cta">
            Pokaži
          </Button>
          <button
            type="button"
            onClick={dismissNudge}
            className="text-muted-foreground hover:text-foreground p-0.5"
            aria-label="Zapri"
            data-testid="install-pwa-nudge-close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md mx-4 max-h-[90vh] overflow-y-auto" data-testid="install-pwa-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-primary" />
              Namesti aplikacijo
            </DialogTitle>
            <DialogDescription>
              Dodaj LD Rakek na domači zaslon naprave za hiter dostop in delovanje brez brskalnika.
            </DialogDescription>
          </DialogHeader>

          {/* Native install (Android/Chrome desktop) */}
          {deferredPrompt && (
            <div className="rounded-lg border border-emerald-300 bg-emerald-50/60 p-3 space-y-2">
              <p className="text-sm font-semibold text-emerald-800">
                Vaš brskalnik podpira hitro namestitev!
              </p>
              <Button
                onClick={handleNativeInstall}
                className="w-full"
                data-testid="install-pwa-native-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Namesti zdaj
              </Button>
            </div>
          )}

          {/* iOS instructions */}
          {(platform === "ios" || (!deferredPrompt && platform !== "android")) && (
            <div className="space-y-2">
              <p className="text-sm font-semibold flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold">iOS</span>
                Safari na iPhone/iPad:
              </p>
              <ol className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="font-bold text-primary">1.</span>
                  <span>Spodaj v Safariju pritisni gumb <Share className="h-4 w-4 inline mx-1" /> <strong>Deli</strong>.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-primary">2.</span>
                  <span>Scroll-aj navzdol in tapni <strong>"Dodaj na začetni zaslon"</strong> <Plus className="h-4 w-4 inline mx-1" />.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-primary">3.</span>
                  <span>Pritisni <strong>"Dodaj"</strong> v zgornjem desnem kotu.</span>
                </li>
              </ol>
            </div>
          )}

          {/* Android instructions */}
          {platform === "android" && !deferredPrompt && (
            <div className="space-y-2 border-t pt-3">
              <p className="text-sm font-semibold flex items-center gap-2">
                <span className="inline-flex h-5 px-1.5 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold">Android</span>
                Chrome / Samsung Browser:
              </p>
              <ol className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="font-bold text-primary">1.</span>
                  <span>Pritisni gumb <strong>⋮ Meni</strong> v zgornjem desnem kotu brskalnika.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-primary">2.</span>
                  <span>Izberi <strong>"Namesti aplikacijo"</strong> ali <strong>"Dodaj na začetni zaslon"</strong>.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-primary">3.</span>
                  <span>Potrdi z <strong>"Namesti"</strong>.</span>
                </li>
              </ol>
            </div>
          )}

          {/* Desktop */}
          {platform === "desktop" && !deferredPrompt && (
            <div className="space-y-2">
              <p className="text-sm font-semibold">Računalnik (Chrome/Edge):</p>
              <p className="text-sm text-muted-foreground">
                V naslovni vrstici brskalnika pritisni ikono <Download className="h-4 w-4 inline" /> ali odpri <strong>⋮ Meni → Namesti LD Rakek</strong>.
              </p>
            </div>
          )}

          <div className="text-[11px] text-muted-foreground border-t pt-2">
            Po namestitvi aplikacijo zaganjate kot katero koli drugo aplikacijo — brez naslovne vrstice brskalnika.
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallPWAButton;
